import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Glass redesign — Phase 2 primitive.
 *
 * Square icon-only button. Standardises the small-action affordance
 * used on cards (`...`), column headers (`+`), topbar (search,
 * filter), and inline editors (back, copy link).
 *
 * Distinct from shadcn `Button` size="icon": the latter inherits
 * `Button`'s variant system and ends up too tall (h-9). This one
 * matches the prototype's `.fx-icon-btn` (28×28) and uses the
 * subtler hover treatment we want for inline UI noise.
 *
 * `tone="brand"` swaps the colour to brand-orange for "primary
 * action" affordances (e.g. the "Filter is active" indicator on
 * the topbar Filter button).
 */

type IconButtonSize = 'xs' | 'sm' | 'md';
type IconButtonTone = 'default' | 'brand' | 'destructive';

const sizeClasses: Record<IconButtonSize, string> = {
  xs: 'size-6',
  sm: 'size-7',
  md: 'size-8',
};

const toneClasses: Record<IconButtonTone, string> = {
  default: 'text-foreground-muted hover:text-foreground hover:bg-accent',
  brand:
    'text-[var(--brand-accent)] hover:bg-[color-mix(in_oklab,var(--brand-accent)_15%,transparent)]',
  destructive: 'text-foreground-muted hover:text-destructive hover:bg-destructive/10',
};

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  size?: IconButtonSize;
  tone?: IconButtonTone;
  /** Required: the icon (typically a lucide-react icon component). */
  children: ReactNode;
  /**
   * Required: accessible label. Icon-only buttons MUST carry one;
   * the type signature enforces it via `aria-label`.
   */
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = 'sm', tone = 'default', className, children, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'inline-flex items-center justify-center rounded-md transition-colors duration-100',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-accent)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          sizeClasses[size],
          toneClasses[tone],
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

IconButton.displayName = 'IconButton';
